/** Etiquetas y formateadores de presentación de Membresías (US-2.4, es-CO). */
import type { MembershipBillingCycle } from '@goelectric/shared-types';

/** Etiqueta visible de cada ciclo de facturación. */
export const BILLING_CYCLE_LABELS: Record<MembershipBillingCycle, string> = {
  MONTHLY: 'Mensual',
  ANNUAL: 'Anual',
};

/** Descripción breve del ciclo para tooltips/ayudas. */
export const BILLING_CYCLE_DESCRIPTIONS: Record<MembershipBillingCycle, string> = {
  MONTHLY: 'Cobro recurrente cada mes.',
  ANNUAL: 'Cobro recurrente una vez al año.',
};

/** Orden estable de los ciclos para selectores. */
export const BILLING_CYCLE_VALUES: readonly MembershipBillingCycle[] = [
  'MONTHLY',
  'ANNUAL',
];

/** Opciones del filtro de estado administrativo (default ACTIVE en el backend). */
export type MembershipStatusFilter = 'ACTIVE' | 'INACTIVE' | 'ALL';

/** Etiqueta visible de cada estado/filtro de membresía. */
export const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatusFilter, string> = {
  ACTIVE: 'Activa',
  INACTIVE: 'Inactiva',
  ALL: 'Todos los estados',
};

/** Descripción del estado para tooltips/ayudas. */
export function membershipStatusDescription(active: boolean): string {
  return active
    ? 'Disponible para consulta y afiliación desde la PWA.'
    : 'Baja lógica: la membresía no se ofrece, pero conserva su historial.';
}

/** Formatea un monto (`$ 49.900`, moneda COP por defecto). */
export function formatCop(
  value: number | null | undefined,
  currency = 'COP',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Un código ISO inválido no debe romper la tabla ni el detalle.
    return `${value} ${currency}`;
  }
}

/** Formatea el descuento porcentual (`15%`); `—` si falta o no es un número. */
export function formatDiscountPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

/** Resumen legible de beneficios (`3 beneficios` / `Sin beneficios`). */
export function formatBenefitsCount(benefits: string[] | null | undefined): string {
  const count = benefits?.filter((benefit) => benefit.trim() !== '').length ?? 0;
  if (count === 0) return 'Sin beneficios';
  return count === 1 ? '1 beneficio' : `${count} beneficios`;
}
